import {
  BoxGeometry,
  type BufferGeometry,
  ConeGeometry,
  OctahedronGeometry,
  SphereGeometry,
  TorusGeometry
} from 'three'
import { type BoneDisplayShape } from '../../../features/skeleton/SkeletonDescriptor'

/**
 * Builds and caches a BufferGeometry for each bone display shape used by the
 * Build Skeleton head handle. All shapes are sized to the same nominal
 * radius so the thickness slider gives consistent visual scaling across
 * shape choices. Caller owns disposal — pass each cached geometry through
 * `.dispose()` when tearing down the editor.
 */
export class BoneShapeGeometry {
  private static readonly cache = new Map<BoneDisplayShape, BufferGeometry>()

  /**
   * Return a shared geometry for the given shape. Geometries are cached
   * across the lifetime of the page (cheap; one geometry per shape).
   */
  static get (shape: BoneDisplayShape, base_radius: number): BufferGeometry {
    const cache_key = shape
    const cached = BoneShapeGeometry.cache.get(cache_key)
    if (cached !== undefined) return cached
    const geometry = BoneShapeGeometry.build(shape, base_radius)
    BoneShapeGeometry.cache.set(cache_key, geometry)
    return geometry
  }

  /** Force-dispose all cached geometries. Used during test teardown. */
  static dispose_all (): void {
    for (const geometry of BoneShapeGeometry.cache.values()) {
      geometry.dispose()
    }
    BoneShapeGeometry.cache.clear()
  }

  private static build (shape: BoneDisplayShape, r: number): BufferGeometry {
    switch (shape) {
      case 'sphere':
        return new SphereGeometry(r, 8, 6)
      case 'cube':
        // Cube edge ≈ 2r so the bounding sphere matches the sphere shape.
        return new BoxGeometry(r * 1.4, r * 1.4, r * 1.4)
      case 'octahedron':
        return new OctahedronGeometry(r * 1.15)
      case 'pyramid':
        // 4-sided cone = pyramid; tip points along +Y.
        return new ConeGeometry(r * 1.1, r * 2.0, 4)
      case 'ring':
        // Torus oriented in XZ plane (lying flat); lookalike for a control
        // ring. Tube radius = ~10% of major radius.
        return new TorusGeometry(r * 1.1, r * 0.18, 6, 16)
      case 'arrow':
        // Two-part arrow: shaft + head. Three.js doesn't have a single
        // arrow geometry, so we merge a thin cylinder shaft with a cone head
        // by manipulating two geometries' positions in a single buffer
        // would be more elegant — but for an MVP head-handle, just use a
        // tall cone pointing up. Looks like an arrowhead at small sizes.
        return new ConeGeometry(r * 0.8, r * 2.4, 8)
    }
    // Unreachable — switch above is exhaustive over BoneDisplayShape.
    return new SphereGeometry(r, 8, 6)
  }
}

